import React from "react";
import { useI18n } from "../i18n/I18nContext";
import { useSite } from "../context/SiteContext";
import PageHero from "../components/PageHero";
import { Building2, Server, Globe, ShieldCheck, Scale, Copyright, UtensilsCrossed, Mail, BedDouble } from "lucide-react";

const CONTENT = {
  fr: {
    title: "Mentions légales",
    subtitle: "Informations légales relatives à l'éditeur et à l'hébergeur du site, conformément à la loi française.",
    crumb: "Mentions légales",
    intro:
      "Conformément aux articles 6-III et 19 de la loi n° 2004-575 du 21 juin 2004 pour la confiance dans l'économie numérique (LCEN), les informations suivantes sont portées à la connaissance des utilisateurs du site.",
    editorTitle: "Éditeur du site",
    editorLines: [
      ["Dénomination", "LE DOMAINE DE LA BÉGAUDIÈRE"],
      ["Forme juridique", "Société par actions simplifiée (SAS)"],
      ["Capital social", "2 000,00 €"],
      ["RCS / SIREN", "108 146 119 R.C.S. Saint-Malo"],
      ["Identifiant EUID", "FR3502.108146119"],
      ["Représentante légale", "Jessica THOMAS"],
      ["Activité", "Chambres d'hôtes, gîtes et location saisonnière"],
      ["Déclaration", "Activité déclarée en mairie de Mont-Dol (35120)"],
    ],
    naturTitle: "Nature et indépendance des hébergements",
    naturText:
      "Le Domaine de La Bégaudière propose deux types d'hébergement distincts, exploités de façon indépendante bien qu'appartenant à la même société. D'une part, une activité de chambres d'hôtes, limitée conformément à la loi à cinq chambres pour une capacité maximale de quinze personnes (articles L.324-3 et D.324-13 du Code du tourisme). D'autre part, une activité de location de meublés de tourisme (gîtes), entièrement séparée des chambres d'hôtes et disposant de ses propres logements, accès et équipements. Ces deux activités sont commercialisées et fonctionnent séparément.",
    directorTitle: "Directeur de la publication",
    directorText: "Jessica THOMAS, en qualité de représentante légale de la société LE DOMAINE DE LA BÉGAUDIÈRE.",
    hostTitle: "Hébergement et nom de domaine",
    hostText: "Le site est hébergé et le nom de domaine est géré par :",
    hostLines: [
      ["Société", "IONOS SARL"],
      ["Adresse", "7 place de la Gare, BP 70109, 57201 Sarreguemines Cedex, France"],
      ["Site web", "www.ionos.fr"],
    ],
    ipTitle: "Propriété intellectuelle",
    ipText:
      "L'ensemble des éléments du site (textes, photographies, logo, éléments graphiques, structure) est la propriété exclusive de la société LE DOMAINE DE LA BÉGAUDIÈRE, sauf mention contraire, et est protégé par le Code de la propriété intellectuelle. Toute reproduction, représentation, modification ou exploitation, totale ou partielle, sans autorisation écrite préalable, est interdite et constitue une contrefaçon.",
    dataTitle: "Données personnelles et cookies",
    dataText:
      "Le traitement de vos données personnelles est détaillé dans notre Politique de confidentialité, établie conformément au RGPD (Règlement UE 2016/679) et à la loi Informatique et Libertés (n° 78-17 du 6 janvier 1978 modifiée). Vous y trouverez les modalités d'exercice de vos droits et l'usage des cookies.",
    haccpTitle: "Hygiène alimentaire",
    haccpText:
      "Dans le cadre des prestations de restauration proposées (petits-déjeuners, table d'hôtes, paniers repas), l'exploitante a suivi une formation à l'hygiène alimentaire (méthode HACCP), conformément à la réglementation applicable aux établissements proposant des denrées alimentaires. Les produits utilisés sont locaux et de saison, dans le respect des règles d'hygiène et de la chaîne du froid. L'établissement est par ailleurs titulaire d'une Licence III (licence restreinte de débit de boissons), l'exploitante ayant obtenu le permis d'exploitation requis pour sa délivrance.",
    consumerTitle: "Information des consommateurs et médiation",
    consumerText:
      "Conformément au Code de la consommation, les prix, prestations et conditions de vente sont communiqués de manière claire avant toute réservation. Conformément aux articles L.616-1 et R.616-1 du Code de la consommation, notre établissement a désigné un médiateur de la consommation (coordonnées ci-dessous). En cas de litige de la consommation non résolu à l'amiable, le client peut le déclarer gratuitement auprès de ce médiateur. Les autorités compétentes (DGCCRF) peuvent également être saisies via la plateforme SignalConso (signal.conso.gouv.fr).",
    lawTitle: "Droit applicable",
    lawText:
      "Le présent site et son utilisation sont soumis au droit français. Tout litige relatif à son utilisation relève de la compétence des tribunaux français.",
    contactTitle: "Contact",
    updated: "Dernière mise à jour : août 2026.",
  },
  en: {
    title: "Legal notice",
    subtitle: "Legal information about the publisher and host of the website, in accordance with French law.",
    crumb: "Legal notice",
    intro:
      "In accordance with Articles 6-III and 19 of French Law No. 2004-575 of 21 June 2004 on confidence in the digital economy (LCEN), the following information is provided to users of the website.",
    editorTitle: "Site publisher",
    editorLines: [
      ["Company name", "LE DOMAINE DE LA BÉGAUDIÈRE"],
      ["Legal form", "Simplified joint-stock company (SAS)"],
      ["Share capital", "€2,000.00"],
      ["Trade register / SIREN", "108 146 119 R.C.S. Saint-Malo"],
      ["EUID", "FR3502.108146119"],
      ["Legal representative", "Jessica THOMAS"],
      ["Activity", "Guest rooms, gîtes and seasonal rental"],
      ["Registration", "Activity declared at Mont-Dol town hall (35120)"],
    ],
    naturTitle: "Nature and independence of the accommodation",
    naturText:
      "Le Domaine de La Bégaudière offers two distinct types of accommodation, operated independently although belonging to the same company. On one hand, a guest-room (chambres d'hôtes) activity, limited in accordance with the law to five rooms with a maximum capacity of fifteen people (Articles L.324-3 and D.324-13 of the French Tourism Code). On the other hand, a self-catering rental (gîtes) activity, entirely separate from the guest rooms, with its own dwellings, access and facilities. These two activities are marketed and operated separately.",
    directorTitle: "Publication director",
    directorText: "Jessica THOMAS, as legal representative of the company LE DOMAINE DE LA BÉGAUDIÈRE.",
    hostTitle: "Hosting and domain name",
    hostText: "The website is hosted and the domain name is managed by:",
    hostLines: [
      ["Company", "IONOS SARL"],
      ["Address", "7 place de la Gare, BP 70109, 57201 Sarreguemines Cedex, France"],
      ["Website", "www.ionos.fr"],
    ],
    ipTitle: "Intellectual property",
    ipText:
      "All elements of the site (texts, photographs, logo, graphics, structure) are the exclusive property of the company LE DOMAINE DE LA BÉGAUDIÈRE, unless otherwise stated, and are protected by the French Intellectual Property Code. Any reproduction, representation, modification or use, in whole or in part, without prior written authorisation, is prohibited and constitutes an infringement.",
    dataTitle: "Personal data and cookies",
    dataText:
      "The processing of your personal data is detailed in our Privacy Policy, established in accordance with the GDPR (EU Regulation 2016/679) and French data protection law (No. 78-17 of 6 January 1978 as amended). You will find there how to exercise your rights and the use of cookies.",
    haccpTitle: "Food hygiene",
    haccpText:
      "As part of the catering services offered (breakfasts, table d'hôtes, meal baskets), the operator has completed food hygiene training (HACCP method), in accordance with the regulations applicable to establishments serving food. Products used are local and seasonal, in compliance with hygiene rules and the cold chain. The establishment also holds a Licence III (restricted licence to serve alcoholic beverages), the operator having obtained the operating permit (permis d'exploitation) required for its issuance.",
    consumerTitle: "Consumer information and mediation",
    consumerText:
      "In accordance with the French Consumer Code, prices, services and sales conditions are communicated clearly before any booking. In accordance with Articles L.616-1 and R.616-1 of the French Consumer Code, our establishment has appointed a consumer mediator (details below). In the event of an unresolved consumer dispute, the customer may refer the matter to this mediator free of charge. The competent authorities (DGCCRF) may also be contacted via the SignalConso platform (signal.conso.gouv.fr).",
    lawTitle: "Applicable law",
    lawText:
      "This website and its use are subject to French law. Any dispute relating to its use falls under the jurisdiction of the French courts.",
    contactTitle: "Contact",
    updated: "Last updated: August 2026.",
  },
};

export default function MentionsPage() {
  const { lang } = useI18n();
  const { settings } = useSite();
  const c = CONTENT[lang] || CONTENT.fr;
  const email = settings.email || "contact@labegaudiere.fr";
  const address = settings.address || "12 La Bégaudière, 35120 Mont-Dol";

  const Block = ({ icon: Icon, title, children, testid }) => (
    <div className="rounded-2xl border border-black/5 bg-white p-6 shadow-sm" data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-50 text-brand"><Icon size={20} /></span>
        <h2 className="font-heading text-xl text-ink-800">{title}</h2>
      </div>
      <div className="mt-4 font-body text-ink-600 leading-relaxed text-sm">{children}</div>
    </div>
  );

  const Lines = ({ rows }) => (
    <dl className="space-y-2">
      {rows.map(([k, v], i) => (
        <div key={i} className="flex flex-col sm:flex-row sm:gap-3">
          <dt className="font-medium text-ink-800 sm:w-40 shrink-0">{k}</dt>
          <dd className="text-ink-600">{v}</dd>
        </div>
      ))}
    </dl>
  );

  return (
    <div data-testid="mentions-page">
      <PageHero title={c.title} subtitle={c.subtitle} image={settings.gallery?.[3] || settings.hero_image} crumb={c.crumb} />
      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 space-y-6">
          <p className="font-body text-ink-600 leading-relaxed text-base">{c.intro}</p>

          <Block icon={Building2} title={c.editorTitle} testid="mentions-editor">
            <Lines rows={c.editorLines} />
            <p className="mt-3">
              <span className="font-medium text-ink-800">{address}</span><br />
              <a href={`mailto:${email}`} className="text-brand underline underline-offset-2">{email}</a>
            </p>
          </Block>

          <Block icon={ShieldCheck} title={c.directorTitle} testid="mentions-director">
            <p>{c.directorText}</p>
          </Block>

          <Block icon={BedDouble} title={c.naturTitle} testid="mentions-nature">
            <p>{c.naturText}</p>
          </Block>

          <Block icon={Server} title={c.hostTitle} testid="mentions-host">
            <p className="mb-3">{c.hostText}</p>
            <Lines rows={c.hostLines} />
          </Block>

          <Block icon={Copyright} title={c.ipTitle} testid="mentions-ip">
            <p>{c.ipText}</p>
          </Block>

          <Block icon={UtensilsCrossed} title={c.haccpTitle} testid="mentions-haccp">
            <p>{c.haccpText}</p>
          </Block>

          <Block icon={Scale} title={c.consumerTitle} testid="mentions-consumer">
            <p>{c.consumerText}</p>
            <div className="mt-4 rounded-xl bg-white border border-brand/15 p-4" data-testid="mentions-mediator">
              <img src="/api/files/begaudiere/uploads/82a6288e-0721-4205-bcba-3f539b4ead4e.webp" alt="CM2C — Centre de la Médiation de la Consommation de Conciliateurs de Justice" className="h-20 w-auto mb-3" loading="lazy" decoding="async" data-testid="mentions-mediator-logo" />
              <p className="font-medium text-ink-800">{lang === "en" ? "Consumer mediator:" : "Médiateur de la consommation :"}</p>
              <p className="mt-1">CM2C — Centre de la Médiation de la Consommation de Conciliateurs de Justice</p>
              <p>49 rue de Ponthieu, 75008 Paris</p>
              <a href="https://www.cm2c.net" target="_blank" rel="noreferrer" className="text-brand underline underline-offset-2" data-testid="mentions-mediator-link">https://www.cm2c.net</a>
            </div>
          </Block>

          <Block icon={Globe} title={c.dataTitle} testid="mentions-data">
            <p>{c.dataText}</p>
          </Block>

          <Block icon={Scale} title={c.lawTitle} testid="mentions-law">
            <p>{c.lawText}</p>
          </Block>

          <p className="mt-8 flex items-center gap-2 font-body text-ink-500 text-sm">
            <Mail size={15} className="text-brand" /> {c.contactTitle} :{" "}
            <a href={`mailto:${email}`} className="text-brand underline underline-offset-2" data-testid="mentions-contact-email">{email}</a>
          </p>
          <p className="mt-2 font-body text-ink-400 text-xs">{c.updated}</p>
        </div>
      </section>
    </div>
  );
}
